import {
    areSearchParamsEqual,
    LEADS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type LeadsFilterItem = { key: string; value: string };

export const LEADS_LIST_TAB = 'Список лідів';
export const LEADS_OWNER_TAB = 'Ліди по Власнику';

export type LeadsUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    campaignId: number | null;
    tab: string;
    childWindow: boolean;
    allTimeChecked: boolean;
    filterContactName: LeadsFilterItem | null;
    filterPhoneNo: LeadsFilterItem | null;
    filterDescription: LeadsFilterItem | null;
    filterIsActive: LeadsFilterItem | null;
    filterPhoneTypeId: LeadsFilterItem[] | null;
    filterIsDone: LeadsFilterItem[] | null;
    filterCallOwner: LeadsFilterItem[] | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    filterLastCallDateTime: Date | null;
    filterLastCallEndDateTime: Date | null;
    startLastCallTime: Date | null;
    endLastCallTime: Date | null;
    filterNewCallDateTime: Date | null;
    filterNewCallEndDateTime: Date | null;
    startNewCallTime: Date | null;
    endNewCallTime: Date | null;
    hasStartFilter: boolean;
    uiFilters: Record<string, any>;
};

export type LeadsUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    campaignId: number | null;
    tab: string;
    childWindow: boolean;
    allTimeChecked: boolean;
    filterContactName: LeadsFilterItem | null;
    filterPhoneNo: LeadsFilterItem | null;
    filterDescription: LeadsFilterItem | null;
    filterIsActive: LeadsFilterItem | null;
    filterPhoneTypeId: LeadsFilterItem[] | null;
    filterIsDone: LeadsFilterItem[] | null;
    filterCallOwner: LeadsFilterItem[] | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
    filterLastCallDateTime: any;
    filterLastCallEndDateTime: any;
    startLastCallTime: any;
    endLastCallTime: any;
    filterNewCallDateTime: any;
    filterNewCallEndDateTime: any;
    startNewCallTime: any;
    endNewCallTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialLeadsUrlState(): LeadsUrlViewState {
    return parseLeadsUrlState(getHashSearchParams());
}

export function parseLeadsUrlState(params: URLSearchParams): LeadsUrlViewState {
    const childWindow = params.get('childWindow') === '1';
    const legacyCampaignId = params.get('campaignId');

    params = unwrapEncodedSearchParams(params, LEADS_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const campaignIdRaw = params.get('campaignId') || legacyCampaignId;
    const campaignId = campaignIdRaw && /^-?\d+$/.test(campaignIdRaw) ? Number(campaignIdRaw) : null;
    const tab = params.get('tab') === 'owner' ? LEADS_OWNER_TAB : LEADS_LIST_TAB;
    const allTimeChecked = parseBooleanParam(params.get('allTime'), false);

    const contactName = params.get('contactName');
    const phoneNo = params.get('phoneNo');
    const description = params.get('description');
    const isActive = params.get('isActive');
    const phoneTypeId = getMultiParams(params, 'phoneTypeId').map(toNumberValue);
    const result = getMultiParams(params, 'result');
    const ownerUserName = getMultiParams(params, 'ownerUserName');
    const startRange = allTimeChecked
        ? getAllTimeRange()
        : parseDateTimeRange(params.get('startFrom'), params.get('startTo'));
    const lastCallRange = parseDateTimeRange(params.get('lastFrom'), params.get('lastTo'));
    const newCallRange = parseDateTimeRange(params.get('newFrom'), params.get('newTo'));

    if (contactName) uiFilters.contactName = contactName;
    if (phoneNo) uiFilters.phoneNo = phoneNo;
    if (description) uiFilters.description = description;
    if (isActive) uiFilters.isActive = isActive;
    if (phoneTypeId.length) uiFilters.phoneTypeId = phoneTypeId;
    if (result.length) uiFilters.result = result;
    if (ownerUserName.length) uiFilters.ownerUserName = ownerUserName;
    if (startRange) uiFilters.createdDateTime = startRange.startDate;
    if (lastCallRange) uiFilters.lastCall = lastCallRange.startDate;
    if (newCallRange) uiFilters.newCall = newCallRange.startDate;

    const hasAnyParams = new URLSearchParams(params);
    hasAnyParams.delete('childWindow');

    return {
        hasAny: hasAnyParams.toString().length > 0 || !!legacyCampaignId,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        campaignId,
        tab,
        childWindow,
        allTimeChecked,
        filterContactName: toFilterItem('ContactName', contactName),
        filterPhoneNo: toFilterItem('PhoneNo', phoneNo),
        filterDescription: toFilterItem('Description', description),
        filterIsActive: toFilterItem('IsActive', isActive),
        filterPhoneTypeId: toFilterList('PhoneTypeId', phoneTypeId),
        filterIsDone: toFilterList('Result', result),
        filterCallOwner: toFilterList('OwnerUserName', ownerUserName),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        filterLastCallDateTime: lastCallRange?.startDate ?? null,
        filterLastCallEndDateTime: lastCallRange?.endDate ?? null,
        startLastCallTime: lastCallRange?.startTime ?? null,
        endLastCallTime: lastCallRange?.endTime ?? null,
        filterNewCallDateTime: newCallRange?.startDate ?? null,
        filterNewCallEndDateTime: newCallRange?.endDate ?? null,
        startNewCallTime: newCallRange?.startTime ?? null,
        endNewCallTime: newCallRange?.endTime ?? null,
        hasStartFilter: !!campaignId,
        uiFilters,
    };
}

export function buildLeadsSearchParams(state: LeadsUrlWriteState): URLSearchParams {
    const encoded = wrapEncodedSearchParams(
        buildPlainLeadsSearchParams(state),
        LEADS_ENCODED_QUERY_KEY
    );
    if (state.childWindow) {
        encoded.set('childWindow', '1');
    }
    return encoded;
}

function buildPlainLeadsSearchParams(state: LeadsUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    if (state.campaignId != null) {
        params.set('campaignId', String(state.campaignId));
    }
    if (state.tab === LEADS_OWNER_TAB) {
        params.set('tab', 'owner');
    }
    if (state.allTimeChecked) {
        params.set('allTime', '1');
    } else if (shouldWriteStartRange(state)) {
        const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
        const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
        if (startFrom) params.set('startFrom', toLocalIso(startFrom));
        if (startTo) params.set('startTo', toLocalIso(startTo));
    }

    setScalar(params, 'contactName', state.filterContactName?.value);
    setScalar(params, 'phoneNo', state.filterPhoneNo?.value);
    setScalar(params, 'description', state.filterDescription?.value);
    setScalar(params, 'isActive', state.filterIsActive?.value);
    setMulti(params, 'phoneTypeId', state.filterPhoneTypeId);
    setMulti(params, 'result', state.filterIsDone);
    setMulti(params, 'ownerUserName', state.filterCallOwner);

    if (hasDateRange(state.filterLastCallDateTime, state.filterLastCallEndDateTime, state.startLastCallTime, state.endLastCallTime)) {
        const lastFrom = combineDateAndTime(state.filterLastCallDateTime, state.startLastCallTime, true);
        const lastTo = combineDateAndTime(state.filterLastCallEndDateTime, state.endLastCallTime, false);
        if (lastFrom) params.set('lastFrom', toLocalIso(lastFrom));
        if (lastTo) params.set('lastTo', toLocalIso(lastTo));
    }

    if (hasDateRange(state.filterNewCallDateTime, state.filterNewCallEndDateTime, state.startNewCallTime, state.endNewCallTime)) {
        const newFrom = combineDateAndTime(state.filterNewCallDateTime, state.startNewCallTime, true);
        const newTo = combineDateAndTime(state.filterNewCallEndDateTime, state.endNewCallTime, false);
        if (newFrom) params.set('newFrom', toLocalIso(newFrom));
        if (newTo) params.set('newTo', toLocalIso(newTo));
    }

    return params;
}

function shouldWriteStartRange(state: LeadsUrlWriteState): boolean {
    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (!startFrom || !startTo) return false;
    if (state.campaignId != null) return true;
    return !isDefaultDateRange(startFrom, startTo);
}

function hasDateRange(startDate: any, endDate: any, startTime: any, endTime: any): boolean {
    return !!(startDate && endDate && startTime && endTime);
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function getAllTimeRange() {
    const startDate = new Date(2023, 5, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    const startTime = new Date();
    startTime.setHours(0, 0, 0, 0);
    const endTime = new Date();
    endTime.setHours(23, 59, 59, 0);
    return { startDate, endDate, startTime, endTime };
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: LeadsFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): LeadsFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): LeadsFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;

    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
